import { useCallback, useEffect, useMemo } from 'react';

import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';

import { useRepullPrefund } from '../queries/useRepullPrefund.query';
import { useRepullReplenishment } from '../queries/useRepullReplenishment.query';

interface Props {
  invoiceId?: string;
  isPrefund: boolean;
  isReplenishment: boolean;
  isRejected: boolean;
  repullManuallyAllowed?: boolean;
  isPrefundExist: boolean;
  refetchInvoice: () => void;
  refetchPrefund: () => void;
}

export const useRepullInvoice = ({
  invoiceId,
  isPrefund,
  isReplenishment,
  isRejected,
  repullManuallyAllowed,
  isPrefundExist,
  refetchInvoice,
  refetchPrefund,
}: Props) => {
  const { handleAddPermanentSnackbar } = useSnackbar();

  const {
    repull: repullPrefund,
    isLoading: repullPrefundLoading,
    isError: repullPrefundError,
    isSuccess: repullPrefundSuccess,
  } = useRepullPrefund();

  const {
    repull: repullReplenishment,
    isLoading: repullReplenishmentLoading,
    isError: repullReplenishmentError,
    isSuccess: repullReplenishmentSuccess,
  } = useRepullReplenishment();

  const handleRepullPrefund = useCallback(async () => {
    if (invoiceId) {
      await repullPrefund(invoiceId);
      refetchInvoice();
      if (isPrefundExist) {
        refetchPrefund();
      }
    }
  }, [invoiceId, isPrefundExist, refetchInvoice, refetchPrefund, repullPrefund]);

  const handleRepullReplenishment = useCallback(async () => {
    if (invoiceId) {
      await repullReplenishment(invoiceId);
      refetchInvoice();
      if (isPrefundExist) {
        refetchPrefund();
      }
    }
  }, [invoiceId, isPrefundExist, refetchInvoice, refetchPrefund, repullReplenishment]);

  const isPrefundAlertVisible = useMemo(
    () => !!repullManuallyAllowed && isPrefund && !repullPrefundSuccess,
    [isPrefund, repullManuallyAllowed, repullPrefundSuccess]
  );

  const isReplenishmentAlertVisible = useMemo(
    () => !!repullManuallyAllowed && !repullReplenishmentSuccess && isRejected && isReplenishment,
    [repullManuallyAllowed, repullReplenishmentSuccess, isRejected, isReplenishment]
  );

  useEffect(() => {
    if (repullPrefundError || repullReplenishmentError) {
      handleAddPermanentSnackbar({
        text: 'Something went wrong. Please try again later.',
        state: SnackbarState.negative,
        closeIcon: true,
      });
    }
    if (repullPrefundSuccess || repullReplenishmentSuccess) {
      handleAddPermanentSnackbar({
        text: 'Successfully resumed',
        state: SnackbarState.positive,
        closeIcon: true,
      });
    }
  }, [
    repullPrefundError,
    handleAddPermanentSnackbar,
    repullPrefundSuccess,
    repullReplenishmentError,
    repullReplenishmentSuccess,
  ]);

  return {
    handleRepullPrefund,
    handleRepullReplenishment,
    isPrefundAlertVisible,
    isReplenishmentAlertVisible,
    repullPrefundLoading,
    repullReplenishmentLoading,
  };
};
